import React from 'react';
import type { AgenticExecutionMetadata } from '@shared/agentic-execution';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ArrowUpRight } from 'lucide-react';
import {
  formatTraceCurrency,
  formatTraceDuration,
} from './ExecutionTrace';

interface AgenticToolUsageProps {
  execution?: AgenticExecutionMetadata | null;
  showCta?: boolean;
  className?: string;
  onOpenTrace?: () => void;
}

const AgenticToolUsage: React.FC<AgenticToolUsageProps> = ({
  execution,
  showCta = false,
  className,
  onOpenTrace,
}) => {
  if (!execution) {
    return null;
  }

  const tools = execution.toolSummary?.uniqueTools ?? execution.toolsUsed ?? [];
  const hasTools = tools.length > 0;
  const hasCta = Boolean(showCta && execution.cta?.text);
  const summary = execution.toolSummary;
  const timings = execution.timings;
  const tokenUsage = execution.tokenUsage;
  const cost = execution.cost;
  const hasSummary = Boolean(summary || timings?.totalMs);
  const hasCost = Boolean(tokenUsage || cost);
  const shouldRender =
    hasTools || hasCta || hasSummary || hasCost || Boolean(onOpenTrace);
  const shouldShowHeader = hasTools || Boolean(onOpenTrace);

  if (!shouldRender) {
    return null;
  }

  const totalSteps =
    summary?.totalSteps ??
    (summary?.steps?.length ? summary.steps.length : null);
  const uniqueToolCount =
    summary?.totalTools ?? (hasTools ? tools.length : null);
  const runtimeLabel = formatTraceDuration(timings?.totalMs ?? undefined);

  return (
    <div
      className={cn(
        'rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 p-3 text-xs text-muted-foreground',
        className,
      )}
    >
      {shouldShowHeader && (
        <div className="flex flex-wrap items-center gap-2">
          {hasTools ? (
            <>
              <span className="font-semibold text-foreground">Tools used:</span>
              {tools.map((tool) => (
                <Badge key={tool} variant="secondary" className="text-[10px] uppercase">
                  {tool}
                </Badge>
              ))}
            </>
          ) : (
            <span className="font-semibold text-foreground">
              Tool telemetry captured
            </span>
          )}
          {onOpenTrace && (
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-orange-700 transition-colors hover:text-orange-900"
              onClick={onOpenTrace}
            >
              View details
              <ArrowUpRight className="h-3 w-3" aria-hidden />
            </button>
          )}
        </div>
      )}

      {hasSummary && (
        <div className="mt-3 grid gap-3 text-[11px] text-muted-foreground sm:grid-cols-3">
          <div>
            <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
              Steps
            </p>
            <p className="text-sm font-semibold text-foreground">
              {typeof totalSteps === 'number' ? totalSteps : '—'}
            </p>
          </div>
          <div>
            <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
              Unique tools
            </p>
            <p className="text-sm font-semibold text-foreground">
              {typeof uniqueToolCount === 'number' ? uniqueToolCount : '—'}
            </p>
          </div>
          <div>
            <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
              Total runtime
            </p>
            <p className="text-sm font-semibold text-foreground">
              {runtimeLabel}
            </p>
          </div>
        </div>
      )}

      {hasCost && (
        <div className="mt-3 rounded-md border border-muted-foreground/30 bg-background/70 p-3 text-[11px] text-muted-foreground">
          {tokenUsage && (
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
                  Input tokens
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {tokenUsage.inputTokens.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
                  Output tokens
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {tokenUsage.outputTokens.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
                  Total tokens
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {tokenUsage.totalTokens.toLocaleString()}
                </p>
              </div>
            </div>
          )}
          {cost && (
            <div className="mt-3">
              <p className="font-semibold uppercase tracking-wide text-[10px] text-foreground/70">
                Estimated cost
              </p>
              <p className="text-sm font-semibold text-foreground">
                {formatTraceCurrency(cost.total, cost.currency)}
              </p>
            </div>
          )}
        </div>
      )}

      {hasCta && (
        <div className="mt-3 rounded-md border border-orange-200/70 bg-orange-50/80 px-3 py-2 text-sm text-orange-900">
          {execution.cta?.text}
        </div>
      )}
    </div>
  );
};

export default AgenticToolUsage;
